import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';

// Plain-English labels mapped over the technical names the server
// stores. The runtime keeps the precise strings; the LIST page shows
// the friendly versions so an MEP foreman / PM doesn't see
// "task_type: drawing_review" or "rowCountRatioEvaluator".
const TASK_TYPE_LABELS = {
  extraction:     'Pull data',
  estimation:     'Estimate labor / cost',
  compliance:     'Code / spec check',
  submittal:      'Submittal review',
  drawing_review: 'Drawing review',
  custom:         'Custom',  // overridden by custom_task_label when present
};
const EVALUATOR_LABELS = {
  rowCountRatioEvaluator:     'Count check',
  citationCoverageEvaluator:  'Citation check',
  checklistCoverageEvaluator: 'Checklist check',
  sheetRefCoverageEvaluator:  'Drawing check',
  customPromptEvaluator:      'Custom check',
};

/**
 * RsiAgentList — admin/builder page that shows every RSI agent the
 * current user has created. Live-fetched from /api/rsi-agents.
 *
 * Behaviour:
 *   - Lists name, category, evaluator, strategy/tool counts, created time
 *   - "+ Build New Agent" CTA → /rsi-agents/new
 *   - Per-row "Open" → /jobotics-agent?agentId=<id> (the chat UI)
 *   - Per-row "Delete" → confirm + DELETE /api/rsi-agents/:id
 *
 * Empty state nudges the user to the builder.
 */
export default function RsiAgentList() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const navigate = useNavigate();

  const fetchAgents = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await fetch('/api/rsi-agents', { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setAgents(Array.isArray(data.agents) ? data.agents : []);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAgents(); }, [fetchAgents]);

  const onDelete = async (id, name) => {
    if (!window.confirm(`Delete agent "${name}"? It will stop appearing in your catalog. Past runs and outputs are preserved.`)) return;
    try {
      const r = await fetch(`/api/rsi-agents/${encodeURIComponent(id)}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      await fetchAgents();
    } catch (e) {
      alert(`Delete failed: ${e.message}`);
    }
  };

  // Clone someone else's agent into a new one owned by the current
  // user. Server returns the new id; we route straight into the edit
  // form so the user can tweak before running.
  const onCopy = async (id, name) => {
    if (!window.confirm(`Copy "${name}" into your own agent? You'll be able to edit the copy without affecting the original.`)) return;
    try {
      const r = await fetch(`/api/rsi-agents/${encodeURIComponent(id)}/clone`, {
        method: 'POST', credentials: 'include',
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
      navigate(`/rsi-agents/${encodeURIComponent(data.agent.id)}/edit`);
    } catch (e) {
      alert(`Copy failed: ${e.message}`);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      <header className="flex items-baseline justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Custom Agents</h1>
          <p className="text-slate-600 mt-1 text-sm">
            MEP agents your team has built. <strong>Anyone on the team can use any agent here</strong> — they're shared by default. Open the chat to run one, or click <strong>Copy &amp; edit</strong> to fork someone else's into your own.
          </p>
        </div>
        <Link
          to="/rsi-agents/new"
          className="rounded-md bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 text-sm font-medium shadow-sm"
        >
          + Build New Agent
        </Link>
      </header>

      {err && (
        <div className="mb-4 rounded-md bg-rose-50 border border-rose-200 text-rose-800 px-4 py-3 text-sm">
          Failed to load: {err}
        </div>
      )}

      {loading ? (
        <div className="text-slate-500 text-sm">Loading…</div>
      ) : agents.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-10 text-center">
          <div className="text-slate-700 font-medium mb-2">No custom agents yet.</div>
          <div className="text-slate-500 text-sm mb-4">
            Spin one up in 5 minutes — pick the job (takeoff, estimate, code check, submittal review, drawings), brief it like a new hire, list 1-3 plays, choose a quality check. We handle the rest.
          </div>
          <Link
            to="/rsi-agents/new"
            className="inline-block rounded-md bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 text-sm font-medium"
          >
            + Build Your First Agent
          </Link>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-600">
              <tr>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Job</th>
                <th className="px-4 py-3 text-left">Quality check</th>
                <th className="px-4 py-3 text-center">Plays</th>
                <th className="px-4 py-3 text-center">Capabilities</th>
                <th className="px-4 py-3 text-left">Created</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {agents.map((a) => (
                <tr key={a.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900 flex items-center gap-2">
                      {a.name}
                      {a.mine ? (
                        <span className="text-[10px] uppercase tracking-wide text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-1.5 py-0.5">Yours</span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-wide text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-1.5 py-0.5">Shared</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500">{a.category} · {a.id}</div>
                    {a.description && <div className="text-xs text-slate-500 mt-1 line-clamp-2">{a.description}</div>}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{a.custom_task_label || TASK_TYPE_LABELS[a.task_type] || a.task_type}</td>
                  <td className="px-4 py-3 text-slate-700 text-xs">{EVALUATOR_LABELS[a.evaluator] || a.evaluator || '—'}</td>
                  <td className="px-4 py-3 text-center text-slate-700">{a.strategy_count}</td>
                  <td className="px-4 py-3 text-center text-slate-700">{a.tool_count}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs">
                    {new Date(a.created_at).toLocaleDateString()}<br />
                    <span className="text-slate-400">{a.mine ? 'you' : a.created_by}</span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => navigate(`/jobotics-agent?agentId=${encodeURIComponent(a.id)}`)}
                      className="text-teal-700 hover:text-teal-900 hover:underline text-sm mr-3"
                    >
                      Open chat
                    </button>
                    {a.mine ? (
                      <>
                        <button
                          onClick={() => navigate(`/rsi-agents/${encodeURIComponent(a.id)}/edit`)}
                          className="text-indigo-700 hover:text-indigo-900 hover:underline text-sm mr-3"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => onDelete(a.id, a.name)}
                          className="text-rose-600 hover:text-rose-800 hover:underline text-sm"
                        >
                          Delete
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => onCopy(a.id, a.name)}
                        title="Make your own editable copy of this agent — the original isn't changed."
                        className="text-indigo-700 hover:text-indigo-900 hover:underline text-sm"
                      >
                        Copy &amp; edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

    </div>
  );
}
